/**
 * A POST that can report its upload and be called off (#1069 Story 4.1,
 * Task 4, AC 9 and AC 11, C2).
 *
 * ⚠️ WHY THIS DOES NOT GO THROUGH `api.ts`, WHICH ALREADY UPLOADS. Two reasons,
 * both acceptance criteria:
 *
 *  · `api.ts` uses a real `XMLHttpRequest` with real progress events ONLY on
 *    web. On iOS and Android it falls back to `fetch` and ESTIMATES progress
 *    from an assumed 500 KB/s (`api.ts` ~line 190). AC 9 says "Never animate a
 *    fake bar" in as many words. React Native implements `XMLHttpRequest`
 *    including `upload.onprogress`, so the honest bar is available on every
 *    platform and this module uses it on every platform.
 *  · `api.ts` exposes no way to abort a request in flight, and AC 11 (FR18)
 *    requires one.
 *
 * It is NOT a general replacement for `api.ts` and must not become one: it
 * exists for the single call that carries files. Everything else in
 * correspondence goes through the provider's transport.
 *
 * ⚠️ NO `.web`/`.native` SIBLINGS, and the XHR is INJECTABLE. Both for the same
 * reason Task 1 learned the hard way: a module with platform siblings cannot be
 * imported by its own name, and no test in `src/` runs against a real DOM or a
 * real device, so the seam is a factory argument rather than a global.
 *
 * ⚠️ IT ENDS, EVEN WHEN NOTHING ELSE DOES (#1069 Story 6.7, D1, D2). Before
 * that story this promise listened for `load`, `error` and `abort` and nothing
 * else, so a connection that simply went quiet -- a server paused mid-body, a
 * mobile stack that dropped the socket without saying so -- left the post
 * `pending` forever: "Sending..." with a bar that never moved and a Cancel on
 * a dead XHR. Two facts shape the fix:
 *
 *  · IT IS A STALL DETECTOR, NOT A WALL CLOCK. The message ceiling is 40 MB
 *    decoded (~54 MB of base64 on the wire), which is 4.5 minutes of
 *    legitimate upload at 200 KB/s and 9 at 100 KB/s, so any whole-request
 *    ceiling short enough to be useful punishes exactly the user the "still
 *    sending" line exists to reassure. The timer is therefore RE-ARMED ON
 *    EVERY `upload.progress` EVENT, and its firing means "no byte has moved
 *    for `UPLOAD_STALL_MS`", which is a dead upload however large the file.
 *  · THERE ARE TWO PHASES, AND THEY END IN DIFFERENT WORDS. While bytes are
 *    still owed, a stall is an honest failure: the server never received the
 *    message and "Not sent" is the truth. After the last byte has left this
 *    device, silence means the server may be inside its transaction (the same
 *    fact `correspondence.md` records for a cancel at 100%), and "Not sent"
 *    would be the lie that delivers the message twice. So the rejection says
 *    which phase it was in (`UploadError.unconfirmed`), and everything the
 *    transport cannot classify -- no progress event yet, an unmeasurable one
 *    -- is treated as POSSIBLY DELIVERED, because that is the cheaper mistake.
 *
 * ⚠️ WHY NOT `xhr.timeout`. It is a wall clock over the whole request, which is
 * the first bullet's problem -- and it dispatches a `timeout` event, NOT an
 * `error` event, on React Native (`XMLHttpRequest.js:690-700`) and in every
 * browser. This module has no `timeout` listener, so anyone who sets
 * `xhr.timeout` here reintroduces the hang this story fixed with one line.
 * It stays 0. A future `timeout` listener would have to classify the phase
 * exactly as the detector below does.
 *
 * ⚠️ WHY THE CLOCK IS CHECKED WHEN THE TIMER FIRES, rather than trusting the
 * timer. Two reasons that look nothing alike and are the same fact: the
 * client test harness caps every timer at 100 ms, so a 60 s timer fires at
 * 100 ms under test; and iOS suspends timers in the background, so a 60 s
 * timer fires late on resume. In both cases "the timer fired" and "N seconds
 * of silence passed" are different facts, and the bound is the second one.
 * The timer is the wake-up; `now()` (injectable, beside `xhrFactory`) is the
 * fact; an early wake-up re-arms for the remainder rather than disabling the
 * detector for the rest of the request.
 */

/** An upload in flight. */
export interface ProgressUpload<T> {
  promise: Promise<T>;
  /** Call it off (AC 11). Nothing is written server-side. */
  abort: () => void;
}

/**
 * No byte moved for this long while bytes were still owed: the upload is dead
 * (#1069 Story 6.7, D1). Re-armed on every progress event, so it is a bound on
 * SILENCE, never on the size of the file.
 */
export const UPLOAD_STALL_MS = 60_000;
/**
 * The last byte left and nothing came back for this long (#1069 Story 6.7,
 * D1). Also the bound before the first progress event, when the phase is not
 * yet known.
 *
 * ⚠️ PROVISIONAL, NOT MEASURED. Task 2.4 asked for three local round trips of a
 * maximal message (6 files, 40 MB decoded, thumbnails running) with the
 * constant set to at least 3x the slowest; the unattended build of this story
 * (2026-09-19) had no browser session to drive the app with and did not take
 * the measurement, so the floor the task set (120 s, and never below it)
 * stands. `deferred-work.md` carries it as the first thing the QA walk
 * re-measures. For scale: Node's own `server.requestTimeout` default is 300 s
 * and IIS ARR's proxy timeout default is 120 s, neither verified on a
 * deployed box.
 */
export const RESPONSE_STALL_MS = 120_000;
/** The upload-phase stall, in the transport's voice; the panel prints its own. */
export const UPLOAD_STALLED_MESSAGE =
  'The upload stopped making progress, so it was called off.';
/** The response-phase stall. NOT "not sent": the server may have committed. */
export const RESPONSE_STALLED_MESSAGE =
  'The upload finished but the server did not answer.';

export class UploadError extends Error {
  /** 0 for an abort, a network failure or a stall. */
  readonly status: number;
  /**
   * ⚠️ THE CALLER MUST BE ABLE TO TELL AN ABORT FROM A FAILURE WITHOUT READING
   * THE MESSAGE. They are different outcomes: AC 11 puts the message back in
   * the composer with nothing said, AC 13 shows an unsent-message line. A
   * string comparison would tie those two behaviours to copy.
   */
  readonly aborted: boolean;
  /**
   * ⚠️ AND A STALL AFTER THE LAST BYTE FROM ONE BEFORE IT, FOR THE SAME REASON
   * (#1069 Story 6.7, D2). `true` means the body may have reached the server
   * and nothing came back: the caller must reconcile and must NOT say "not
   * sent". `false` covers every other failure, including a network `error`
   * event, which is a fact about the request rather than silence. Never
   * `true` together with `aborted`.
   */
  readonly unconfirmed: boolean;
  /**
   * The parsed JSON error body, whole, on an HTTP refusal (#1599 Story 1.4),
   * so a structured refusal (`MENTION_NOT_ALLOWED`'s `refused` ids) reaches
   * the caller on the files path exactly as `ApiError.body` carries it on the
   * text path. Additive; absent for an abort, a network failure or a stall.
   */
  readonly body?: unknown;

  constructor(
    status: number,
    message: string,
    aborted = false,
    unconfirmed = false,
    body?: unknown,
  ) {
    super(message);
    this.name = 'UploadError';
    this.status = status;
    this.aborted = aborted;
    this.unconfirmed = unconfirmed;
    this.body = body;
  }
}

/**
 * `null` means the transport cannot measure this upload, and the caller should
 * show the indeterminate spinner rather than invent a number (AC 9).
 */
export type ProgressPercent = number | null;

export const postJsonWithProgress = <T>(args: {
  url: string;
  headers: Record<string, string>;
  payload: unknown;
  /**
   * The verb (#1530). `POST` by default, which is every caller before #1530;
   * an edit that ADDS files is a `PATCH` with the same base64-in-JSON body,
   * the same ceilings and the same stall detector, so it rides this transport
   * rather than `api.ts`'s unbounded-progress `fetch`.
   */
  method?: 'POST' | 'PATCH';
  onProgress?: (percent: ProgressPercent) => void;
  /** Test seam. Defaults to the platform's own. */
  xhrFactory?: () => XMLHttpRequest;
  /**
   * Test seam beside `xhrFactory` (Story 6.7, D1): the clock the stall
   * detector reads when its timer fires. Defaults to `Date.now`.
   */
  now?: () => number;
}): ProgressUpload<T> => {
  const { url, headers, payload, onProgress, xhrFactory } = args;
  const method = args.method ?? 'POST';
  const now = args.now ?? Date.now;
  const xhr = (xhrFactory ?? (() => new XMLHttpRequest()))();

  let settled = false;

  const promise = new Promise<T>((resolve, reject) => {
    // ── The stall detector (Story 6.7, D1) ────────────────────────────────
    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    let lastActivityAt = now();
    /**
     * What the last MEASURABLE progress event said, or `null` when the
     * transport has never measured this upload or the last event said it
     * cannot. An unmeasurable event RESETS this to `null` on purpose: the last
     * thing the transport knows is that it cannot tell.
     */
    let lastMeasured: { loaded: number; total: number } | null = null;
    /** Bytes are known to be owed. Anything else is "possibly delivered". */
    const stillUploading = () =>
      lastMeasured !== null && lastMeasured.loaded < lastMeasured.total;
    const bound = () =>
      stillUploading() ? UPLOAD_STALL_MS : RESPONSE_STALL_MS;
    const disarm = () => {
      if (stallTimer !== null) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };

    const finish = (fn: () => void) => {
      // Once settled, later events are noise: an `abort` is commonly followed
      // by a `load` on some stacks, and that must not resolve a cancelled post.
      if (settled) return;
      settled = true;
      // No timer outlives the request, whichever way it ended.
      disarm();
      fn();
    };

    const onStallTimer = () => {
      stallTimer = null;
      if (settled) return;
      const idle = now() - lastActivityAt;
      const limit = bound();
      if (idle < 0) {
        // The wall clock stepped BACKWARDS (an NTP correction, a manual
        // change): the most recent activity is "now" as far as the detector
        // can know, and the bound restarts in full. Never `limit - idle`,
        // which would be longer than the bound by the size of the step
        // (review of Story 6.7).
        lastActivityAt = now();
        arm(limit);
        return;
      }
      if (idle < limit) {
        // Woke early (the harness's cap) or late (an iOS resume that fired a
        // suspended timer before the gap was real): the CLOCK is the fact.
        arm(limit - idle);
        return;
      }
      const unconfirmed = !stillUploading();
      // Settle FIRST, then tear down: RN dispatches `abort` synchronously from
      // `xhr.abort()`, and once settled that event is noise rather than a
      // cancel.
      finish(() =>
        reject(
          new UploadError(
            0,
            unconfirmed ? RESPONSE_STALLED_MESSAGE : UPLOAD_STALLED_MESSAGE,
            false,
            unconfirmed,
          ),
        ),
      );
      xhr.abort();
    };

    const arm = (delay: number = bound()) => {
      disarm();
      stallTimer = setTimeout(onStallTimer, delay);
    };

    xhr.upload?.addEventListener('progress', (event: ProgressEvent) => {
      // A byte moved: the detector starts counting again from here, on the
      // bound for whichever phase this event reports.
      lastActivityAt = now();
      const measurable = event.lengthComputable && event.total > 0;
      lastMeasured = measurable
        ? { loaded: event.loaded, total: event.total }
        : null;
      // Not after a settle: `finish` disarmed, and a progress event a browser
      // queued before `abort()` landed must not arm a timer that outlives
      // the request (review of Story 6.7).
      if (!settled) arm();

      if (!onProgress) return;
      if (!measurable) {
        // Not measurable. Say so, rather than guessing (AC 9).
        onProgress(null);
        return;
      }
      const percent = Math.round((event.loaded / event.total) * 100);
      // A transport that over-reports must not drive the bar past full.
      onProgress(Math.max(0, Math.min(100, percent)));
    });

    xhr.addEventListener('load', () =>
      finish(() => {
        const parsed = ((): unknown => {
          try {
            return JSON.parse(xhr.responseText);
          } catch {
            // A gateway's HTML error page, or an empty 204.
            return undefined;
          }
        })();
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(parsed as T);
          return;
        }
        const reason =
          (parsed as { error?: string } | undefined)?.error ??
          `Request failed (${xhr.status})`;
        reject(new UploadError(xhr.status, reason, false, false, parsed));
      }),
    );

    xhr.addEventListener('error', () =>
      finish(() =>
        reject(new UploadError(0, 'A network error stopped the upload.')),
      ),
    );

    xhr.addEventListener('abort', () =>
      finish(() => reject(new UploadError(0, 'Upload cancelled.', true))),
    );

    xhr.open(method, url, true);
    for (const [name, value] of Object.entries(headers)) {
      xhr.setRequestHeader(name, value);
    }
    // The session cookie rides along, as every other correspondence call does.
    xhr.withCredentials = true;
    // ⚠️ `xhr.timeout` IS DELIBERATELY LEFT AT 0: see the header. The stall
    // detector below is the bound, and it is armed the moment the request
    // leaves, on the response bound because no phase is known yet.
    xhr.send(JSON.stringify(payload));
    // `send()` can settle synchronously on some stacks (an `error` dispatched
    // from an invalid state); a timer armed after `finish` would be an orphan.
    if (!settled) {
      lastActivityAt = now();
      arm();
    }
  });

  return {
    promise,
    abort: () => xhr.abort(),
  };
};
